import type { DataMutate, UUID } from "../../../../../types/types";
import type {
  AttributeGroupSummary,
  PIMProduct,
} from "../../../../../types/types.PIM";
import type { EntityProfile, EntityType } from "../../../../../types/types.MDM";
import { ENTITY_MODIFY_PERMISSIONS } from "../../../../../types/types.MDM";
import { PROFILE_DETAIL_CLASSES } from "../../../../../layout/shared/ProfileDetailLayout";
import {
  AttributeGroupCard,
  AttributeGroupDetails,
} from "../../../../../components/AttributeGroupCard";
import { useAuthContext } from "../../../../../components/Auth";
import axios, { AxiosError } from "axios";
import { useStoreState } from "../../../../../util/util";
import { getAssetAssociateEndpoint } from "../../../../../util/assetEndpoints";
import { useContext } from "react";
import { useParams } from "react-router-dom";
import { Notifications } from "../../../../../components/Notifications/NotificationsContext";

interface EntityGroupCardProps {
  entity: EntityProfile;
  entityType: EntityType;
  group: AttributeGroupSummary;
  mutateEntity: DataMutate<EntityProfile>;
  groupEndpoint: string;
}

function entityToDocumentFormProduct(entity: EntityProfile): PIMProduct {
  return {
    id: entity.id,
    slug: entity.id,
    product_number: null,
    status: entity.status,
    is_editable: entity.status !== "archived",
    pim_product_type: "standard",
    cover_image: {} as PIMProduct["cover_image"],
  } as unknown as PIMProduct;
}

export function entityAttributeHasDisplayValue(
  attribute: AttributeGroupSummary["attributes"][number]
): boolean {
  if (attribute.input_type === "document") {
    return (
      attribute.values?.some(
        (entry) => (entry.value as { asset_id?: string } | null)?.asset_id
      ) ?? false
    );
  }

  if (attribute.input_type === "object_reference") {
    return attribute.values?.some((entry) => !!entry.object_ref?.id) ?? false;
  }

  if (attribute.input_type === "product_reference") {
    return attribute.values?.some((entry) => !!entry.product_ref?.id) ?? false;
  }

  return attribute.values?.length > 0 && !!attribute.values?.[0]?.value;
}

/**
 * Details component wrapper for entities that uses shared AttributeGroupDetails
 */
function EntityAttributeGroupDetails({
  group,
  canModify = false,
  showMandatoryAsterisk = false,
}: {
  group: AttributeGroupSummary;
  canModify: boolean;
  showMandatoryAsterisk: boolean;
}): JSX.Element {
  const { roleIsGuest, roleIsSomeKindOfBuyer, roleIsSomeKindOfSeller } =
    useAuthContext();

  return (
    <AttributeGroupDetails
      group={group}
      isPortfolio={false}
      showMandatoryAsterisk={showMandatoryAsterisk}
      shouldHideEmptyAttributes={!canModify}
      disableFiltering={false}
      roleIsGuest={roleIsGuest}
      roleIsSomeKindOfBuyer={roleIsSomeKindOfBuyer}
      roleIsSomeKindOfSeller={roleIsSomeKindOfSeller}
    />
  );
}

/**
 * Entity group card - thin wrapper around generic AttributeGroupCard
 * Now uses the same AttributeGroupDetails as products for identical appearance
 */
export function EntityGroupCard({
  entity,
  entityType,
  group,
  mutateEntity,
  groupEndpoint,
}: EntityGroupCardProps): JSX.Element {
  const { entity_id } = useParams<{ entity_id: string }>();
  const { hasPermission } = useAuthContext();
  const { storefront_metadata } = useStoreState();

  const isEditable =
    hasPermission(ENTITY_MODIFY_PERMISSIONS[entityType]) &&
    entity.status !== "archived";

  const { tenant_id } = useStoreState();
  const { notifyError } = useContext(Notifications);

  const associateEndpoint = getAssetAssociateEndpoint(
    entityType,
    tenant_id,
    entity.id
  );

  const onAssociateDocumentAssets = async (assetIds: string[]) => {
    await axios.patch(associateEndpoint, { asset_ids: assetIds });
  };

  const updateApplicability = async (
    attributesToMakeApplicable: UUID[],
    attributesToMakeUnapplicable: UUID[]
  ) => {
    try {
      await axios.put(
        `/v2/tenants/${tenant_id}/${entityType + "s"}/${entity.id}/groups/${
          group.id
        }/applicability`,
        {
          attributes_to_make_applicable: attributesToMakeApplicable,
          attributes_to_make_unapplicable: attributesToMakeUnapplicable,
        }
      );
      await mutateEntity();
    } catch (error) {
      const errorMessage = (error as AxiosError)?.response?.data?.message;
      notifyError(
        errorMessage || "There was an error updating the applicability"
      );
    }
  };

  return (
    <div className={PROFILE_DETAIL_CLASSES.groupCard}>
      <AttributeGroupCard
        data={entity}
        group={group}
        mutate={mutateEntity}
        groupEndpoint={groupEndpoint}
        detailsComponent={({ group }) => (
          <EntityAttributeGroupDetails
            group={group}
            canModify={isEditable}
            showMandatoryAsterisk={
              entity.schema.show_mandatory_completion_score
            }
          />
        )}
        showMandatoryAsterisk={entity.schema.show_mandatory_completion_score}
        filterInvisibleAttributesInEditMode={true}
        isEditable={isEditable}
        updateApplicability={updateApplicability}
        isChangeRequestEnabled={storefront_metadata.enable_change_requests}
        onAssociateDocumentAssets={onAssociateDocumentAssets}
        product={entityToDocumentFormProduct(entity)}
        objectSearchExcludeProfileId={entity_id ?? entity.id}
      />
    </div>
  );
}
